/**
 * Envelope<T> — wire shape every native REST endpoint returns.
 *
 * The server wraps responses in `{ success, errors, payload, statusCode }`
 * via `Envelope.fromResult(...)`. Generated SDK response types reflect
 * this shape directly (rather than hiding the envelope and exposing only
 * the payload). Callers decide whether to inspect `success`/`errors` or
 * grab `payload` via the `unwrapEnvelope` helper.
 */

export interface Envelope<T> {
	errors: string[];
	payload: T | null;
	statusCode: number;
	success: boolean;
}

/**
 * Throw on envelope failure or missing payload; return the typed payload.
 *
 * Use this when a call site treats failures as exceptions. For granular
 * control, check `env.success` and `env.errors` directly.
 */
export function unwrapEnvelope<T>(env: Envelope<T>): T {
	if (!env.success) {
		throw new Error(env.errors[0] ?? "Request failed");
	}
	if (env.payload === null) {
		throw new Error("Envelope marked success but payload is null");
	}
	return env.payload;
}

/**
 * Coerce an SDK `error` value into a real `Error` with a human message.
 *
 * On a non-2xx response the hey-api client parses the response body and
 * hands it back as `error`. For native REST endpoints that body is a
 * failure `Envelope` (`{ success, errors, payload, statusCode, code }`),
 * so `String(error)` would yield the useless `"[object Object]"`. Pull the
 * first `errors[]` entry (falling back to `message`) so callers surface the
 * real server message, and preserve `statusCode`/`code` on the thrown Error
 * for call sites that branch on them.
 */
function toError(error: unknown): Error {
	if (error instanceof Error) {
		return error;
	}
	if (typeof error === "string") {
		return new Error(error);
	}
	if (error && typeof error === "object") {
		const body = error as {
			errors?: unknown;
			message?: unknown;
			statusCode?: unknown;
			code?: unknown;
		};
		const fromErrors =
			Array.isArray(body.errors) &&
			typeof body.errors[0] === "string" &&
			body.errors[0].length > 0
				? body.errors[0]
				: undefined;
		const message =
			fromErrors ??
			(typeof body.message === "string" && body.message.length > 0
				? body.message
				: undefined);
		const err = new Error(message ?? safeStringify(error));
		if (typeof body.statusCode === "number") {
			(err as Error & { statusCode?: number }).statusCode = body.statusCode;
		}
		if (typeof body.code === "string") {
			(err as Error & { code?: string }).code = body.code;
		}
		return err;
	}
	return new Error(String(error));
}

function safeStringify(value: unknown): string {
	try {
		return JSON.stringify(value);
	} catch {
		return "Request failed";
	}
}

/**
 * Hey-api SDK functions return `{ data?, error?, response }`. This helper
 * runs the SDK call, throws on transport error, and unwraps the envelope.
 * Use it as a drop-in replacement for `apiFetch` callers that want the
 * payload directly.
 *
 * @example
 *   const prefs = await callApi(() => accountNotificationsControllerGet())
 */
export async function callApi<T>(
	fn: () => Promise<{
		data?: Envelope<T> | undefined;
		error?: unknown;
		response?: Response;
	}>
): Promise<T> {
	const { data, error } = await fn();
	if (error !== undefined && error !== null) {
		throw toError(error);
	}
	if (data === undefined) {
		throw new Error("Empty response body");
	}
	return unwrapEnvelope(data);
}
